import { yupResolver } from "@hookform/resolvers/yup";
import Button from "components/Button";
import Input from "components/Input";
import { useToaster } from "contexts/toasterContext";
import React, { useState } from "react";
import { preventSpecialCharKeyDown } from "util/preventSpecialCharInput";
import { FormProvider, useForm } from "react-hook-form";
import apiService from "services/apiService";
import * as yup from "yup";

// Converts empty/blank inputs to `undefined` so `.required()` fires instead of
// yup's confusing "must be a number" cast error on an empty field.
// NOTE: mirrors the helper used in AddTaxSetup so the edit form validates the
// same way as the add/config form.
const toNumberOrUndefined = (value: unknown, originalValue: unknown) =>
  originalValue === "" || originalValue === null || originalValue === undefined
    ? undefined
    : value;

const currentYear = new Date().getFullYear();

// ─── Validation schema ─────────────────────────────────────────────────────
// Mirrors the AddTaxSetup config form, but flattened to a SINGLE slab
// (slabStart + taxRate) because editing operates on one existing tax-config row.
// There is intentionally no `slabs` array here — new slabs cannot be added while
// editing; only the existing slab's values are shown and editable.
const schema = yup.object().shape({
  financialYear: yup
    .number()
    .transform(toNumberOrUndefined)
    .typeError("Financial Year is required")
    .integer("Financial Year must be a whole number")
    .min(2000, "Financial Year must be 2000 or later")
    .max(currentYear + 1, "Invalid Financial Year")
    .required("Financial Year is required"),
  standardDeduction: yup
    .number()
    .transform(toNumberOrUndefined)
    .typeError("Standard Deduction is required")
    .min(0, "Standard Deduction cannot be negative")
    .required("Standard Deduction is required"),
  cessRate: yup
    .number()
    .transform(toNumberOrUndefined)
    .typeError("Cess Rate is required")
    .min(0, "Cess Rate cannot be negative")
    .max(100, "Cess Rate cannot exceed 100%")
    .required("Cess Rate is required"),
  rebateLimit: yup
    .number()
    .transform(toNumberOrUndefined)
    .typeError("Rebate Limit is required")
    .min(0, "Rebate Limit cannot be negative")
    .required("Rebate Limit is required"),
  rebateIncomeThreshold: yup
    .number()
    .transform(toNumberOrUndefined)
    .typeError("Rebate Income Threshold is required")
    .min(0, "Rebate Income Threshold cannot be negative")
    .required("Rebate Income Threshold is required"),
  slabStart: yup
    .number()
    .transform(toNumberOrUndefined)
    .typeError("Slab Start is required")
    .min(0, "Slab Start must be 0 or more")
    .required("Slab Start is required"),
  taxRate: yup
    .number()
    .transform(toNumberOrUndefined)
    .typeError("Tax Rate is required")
    .min(0, "Tax Rate must be 0 or more")
    .max(100, "Tax Rate cannot exceed 100%")
    .required("Tax Rate is required"),
});

type TaxProps = yup.InferType<typeof schema>;

interface UpdateTaxConfigProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateData: any;
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setRefresh: any;
  refresh: number;
}

const UpdateTaxConfig = ({
  updateData,
  onClose,
  refresh,
  setRefresh,
}: UpdateTaxConfigProps) => {
  const { addToast } = useToaster();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<TaxProps>({
    resolver: yupResolver(schema),
    // Seed EVERY field from the row being edited so the form opens pre-filled
    // with the existing values. Fallback to "" keeps the input controlled and
    // lets required-field validation fire if a value happens to be missing.
    defaultValues: {
      financialYear: updateData?.financialYear ?? currentYear,
      standardDeduction: updateData?.standardDeduction ?? ("" as unknown as number),
      cessRate: updateData?.cessRate ?? ("" as unknown as number),
      rebateLimit: updateData?.rebateLimit ?? ("" as unknown as number),
      rebateIncomeThreshold:
        updateData?.rebateIncomeThreshold ?? ("" as unknown as number),
      slabStart: updateData?.slabStart ?? ("" as unknown as number),
      taxRate: updateData?.taxRate ?? ("" as unknown as number),
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // Extract isDirty from formState to track modifications
  const { isDirty } = methods.formState;

  const submit = (taskData: TaxProps) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    apiService
      .put(`api/tax/${updateData.id}`, taskData)
      .then(() => {
        setRefresh(refresh + 1);
        addToast("Tax Config is Updated successfully", "success");
      })
      .catch((error) => {
        addToast(
          error?.response?.data?.message || "Failed to update tax config",
          "error",
        );
      })
      .finally(() => {
        setIsSubmitting(false);
        methods.reset();
        onClose();
      });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(submit)}>
        {/* ── General settings (same field set as the add/config form) ─────── */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0 p-4 pb-0">
          <Input
            name="financialYear"
            label="Financial Year"
            placeholder="Enter Financial Year"
            type="number"
            required
            compact
            min={2000}
            max={currentYear + 1}
            onKeyDown={preventSpecialCharKeyDown}
          />
          <Input
            name="standardDeduction"
            label="Standard Deduction"
            placeholder="Enter Standard Deduction (₹)"
            type="number"
            required
            compact
            min={0}
            onKeyDown={preventSpecialCharKeyDown}
          />
          <Input
            name="cessRate"
            label="Cess Rate (%)"
            placeholder="Enter Cess Rate (%)"
            type="number"
            required
            compact
            min={0}
            max={100}
            onKeyDown={preventSpecialCharKeyDown}
          />
          <Input
            name="rebateLimit"
            label="Rebate Limit"
            placeholder="Enter Rebate Limit (₹)"
            type="number"
            required
            compact
            min={0}
            onKeyDown={preventSpecialCharKeyDown}
          />
          <Input
            name="rebateIncomeThreshold"
            label="Rebate Income Threshold"
            placeholder="Enter Rebate Income Threshold (₹)"
            type="number"
            required
            compact
            min={0}
            onKeyDown={preventSpecialCharKeyDown}
          />
        </section>

        {/* ── Tax slab ─────────────────────────────────────────────────────── */}
        {/* Single existing slab only. Unlike the add/config form there is no
            "Add Slab" / remove control — while editing, new slabs cannot be
            added; the existing slab's values are simply shown and editable. */}
        <section className="px-4 pt-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Tax Slab
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0">
            <Input
              name="slabStart"
              label="Slab Start"
              placeholder="e.g. 400000"
              type="number"
              required
              compact
              min={0}
              onKeyDown={preventSpecialCharKeyDown}
            />
            <Input
              name="taxRate"
              label="Tax Rate (%)"
              placeholder="e.g. 5"
              type="number"
              required
              compact
              min={0}
              max={100}
              onKeyDown={preventSpecialCharKeyDown}
            />
          </div>
        </section>

        {/* ── Actions ──────────────────────────────────────────────────────── */}
        <section className="mt-3 flex justify-end px-4 pb-2">
          <Button
            type="button"
            onClick={onClose}
            className="!bg-white !text-slate-600 border border-slate-200 hover:!bg-slate-100 focus:!ring-4 focus:!outline-none focus:!ring-slate-200 transition-all active:scale-95 mr-2"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || !isDirty}
            loading={isSubmitting}
            className="!text-white !bg-blue-700 hover:!bg-blue-800 focus:!ring-4 focus:!outline-none focus:!ring-blue-300 dark:!bg-blue-600 dark:hover:!bg-blue-700 dark:focus:!ring-blue-800 disabled:!opacity-50 disabled:!cursor-not-allowed"
          >
            Submit
          </Button>
        </section>
      </form>
    </FormProvider>
  );
};

export default UpdateTaxConfig;
